import React, { useState, useEffect, useMemo } from 'react';
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  updateDoc
} from 'firebase/firestore';
import Select from 'react-select';
import { db } from '../../firebase';
import { getLiveCollection } from '../../utils/liveData';
import { formatPrice, formatDate, formatDateForInput } from '../../utils';
import { fetchAtolyeler, updateAtolyeler } from '../../utils/atolye';
import ModernSelect from '../common/ModernSelect';
import OrderCosts from './OrderCosts';
import './Expenses.css';
import './ExpensesModern.css';

// Mağaza Giderleri Kategorileri
const STORE_CATEGORIES = [
  'Mağaza Kirası',
  'Personel Maaşları & Prim',
  'Elektrik / Su / İnternet',
  'Reklam & Sosyal Medya',
  'Muhasebe & Vergi / Aidat',
  'Yemek & Ulaşım',
  'İkram & Mağaza İçi Sarf',
  'Dekorasyon & Tadilat',
  'Firma Bağlantısı',
  'Diğer Mağaza Giderleri'
];

// Atölye Giderleri Kategorileri
const ATELIER_CATEGORIES = [
  'Dikim / İşçilik Ücreti',
  'Kumaş / Malzeme Alımı',
  'Terzi & İşçilik',
  'Atölye Kirası',
  'Elektrik / Su / Doğalgaz',
  'Makine & Ekipman Bakım',
  'Kargo & Lojistik',
  'Diğer Atölye Giderleri'
];

// Montaj Giderleri Kategorileri (ödenmiş olanlar anasayfada net kazançtan düşülür)
const MONTAJ_CATEGORIES = [
  'Montajcı Ücreti / Yevmiye',
  'Yol & Yakıt',
  'Araç Kirası / Bakım',
  'Otopark / Köprü / Otoyol',
  'Montaj Malzemesi (Vida, Dübel vb.)',
  'Yemek',
  'Diğer Montaj Giderleri'
];

// Firma Giderleri: firmalara yapılan ödemeler (firma carisi). Firmalar Anasayfa > Firma Bilgileri'nden gelir.
// Sipariş maliyetleri zaten net kazançtan düşüldüğü için bu kayıtlar anasayfa hesaplarına eklenmez.
const FIRMA_CATEGORIES = [
  'Ürün / Kumaş Ödemesi',
  'Mekanizma / Motor Ödemesi',
  'Aksesuar Ödemesi',
  'Avans / Ön Ödeme',
  'Diğer Firma Ödemeleri'
];

// Sekme adları: Mağaza (store), Atölye (atelier), Montaj (montaj), Firma (firma)
const SUBTAB_LABELS = { store: 'Mağaza', atelier: 'Atölye', montaj: 'Montaj', firma: 'Firma' };
const SUBTAB_FILE_NAMES = { store: 'Magaza', atelier: 'Atolye', montaj: 'Montaj', firma: 'Firma' };

// Atölye seçim kutusu: formdaki diğer alanlarla aynı görünüm, proje renkleri
const atolyeSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 12,
    borderColor: state.isFocused ? '#ea580c' : 'rgba(24, 24, 27, 0.12)',
    backgroundColor: '#ffffff',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(234, 88, 12, 0.14)' : 'none',
    fontSize: '0.9rem',
    cursor: 'pointer',
    '&:hover': { borderColor: state.isFocused ? '#ea580c' : 'rgba(24, 24, 27, 0.24)' },
  }),
  valueContainer: (base) => ({ ...base, padding: '2px 12px' }),
  singleValue: (base) => ({ ...base, color: '#18181b' }),
  placeholder: (base) => ({ ...base, color: '#a1a1aa' }),
  menu: (base) => ({
    ...base,
    borderRadius: 14,
    overflow: 'hidden',
    border: '1px solid rgba(24, 24, 27, 0.08)',
    boxShadow: '0 16px 40px -12px rgba(24, 24, 27, 0.28)',
  }),
  menuList: (base) => ({ ...base, padding: 6 }),
  // .modal-overlay başka sayfaların CSS'inde z-index: 9999 alıyor; menü pencerenin üstünde kalsın
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
  option: (base, state) => ({
    ...base,
    borderRadius: 9,
    fontSize: '0.88rem',
    cursor: 'pointer',
    backgroundColor: state.isFocused ? '#f4f4f5' : '#ffffff',
    color: '#18181b',
    fontWeight: state.isSelected ? 600 : 400,
    ':active': { backgroundColor: '#e4e4e7' },
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base, state) => ({ ...base, color: state.isFocused ? '#18181b' : '#71717a' }),
};

const PAYMENT_METHODS = [
  { id: 'nakit', label: 'Nakit' },
  { id: 'kredi_karti', label: 'Kredi Kartı' },
  { id: 'havale', label: 'Banka Havalesi / EFT' },
  { id: 'diger', label: 'Diğer' }
];

const Expenses = () => {
  const [activeSubTab, setActiveSubTab] = useState('store'); // 'store' (varsayılan) | 'atelier' | 'montaj' | 'orderCosts' (yalnızca görüntüleme) | 'firma'
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Atölyeler (settings/global.atolyeler) ve seçili atölye filtresi
  const [atolyeler, setAtolyeler] = useState([]);
  const [selectedAtolyeId, setSelectedAtolyeId] = useState('all');
  const [newAtolyeName, setNewAtolyeName] = useState('');
  const [editingAtolye, setEditingAtolye] = useState(null); // { id, name }
  const [deleteConfirmAtolye, setDeleteConfirmAtolye] = useState(null);

  // Firmalar (Anasayfa > Firma Bilgileri, 'companies' koleksiyonu) ve seçili firma filtresi
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('all');

  // Filtreleme ve Arama Eyaletleri
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all'); // 'all' | 'paid' | 'pending'
  const [dateRange, setDateRange] = useState('all'); // 'all' | 'this-month' | 'last-month' | 'this-year'
  const [sortOrder, setSortOrder] = useState('date-desc'); // 'date-desc' | 'date-asc' | 'amount-desc' | 'amount-asc'

  // Sayfalama (Pagination) Eyaletleri
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal Eyaletleri
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null); // null => Ekleme, Object => Düzenleme
  const [deleteConfirmExpense, setDeleteConfirmExpense] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    category: STORE_CATEGORIES[0],
    amount: '',
    date: formatDateForInput(new Date()),
    status: 'paid', // 'paid' | 'pending'
    paymentMethod: 'nakit',
    vendor: '',
    atolyeId: '',
    companyId: '',
    notes: ''
  });

  const fetchCompanies = () => getLiveCollection('companies')
    .then(snap => setCompanies(
      snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'))
    ))
    .catch(error => console.error('Firmalar yüklenirken hata oluştu:', error));

  // Firestore'dan Giderleri Çek
  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getLiveCollection('expenses');
      const list = [];
      querySnapshot.forEach((docSnap) => {
        list.push({
          id: docSnap.id,
          ...docSnap.data()
        });
      });
      setExpenses(list);
    } catch (error) {
      console.error('Giderler yüklenirken hata oluştu:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
    fetchAtolyeler()
      .then(setAtolyeler)
      .catch(error => console.error('Atölyeler yüklenirken hata oluştu:', error));
    fetchCompanies();
  }, []);

  // Atölye adı her zaman güncel listeden gelir (gider kaydındaki ad, atölye silinmişse yedek olarak kullanılır)
  const getExpenseAtolyeName = (item) =>
    atolyeler.find(a => a.id === item.atolyeId)?.name || item.atolyeName || item.vendor || '';

  // Firma adı her zaman güncel listeden gelir (firma silinmişse kayıttaki ad kullanılır)
  const getExpenseCompanyName = (item) =>
    companies.find(c => c.id === item.companyId)?.name || item.companyName || item.vendor || '';

  // Tablo / CSV'deki "kime" bilgisi: atölye, firma ya da serbest yazılan tedarikçi
  const getExpensePartyName = (item) =>
    activeSubTab === 'atelier' ? getExpenseAtolyeName(item) : activeSubTab === 'firma' ? getExpenseCompanyName(item) : (item.vendor || '');

  // ---- Atölye ekle / düzenle / sil ----
  const addAtolye = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return null;
    const existing = atolyeler.find(a => a.name.toLocaleLowerCase('tr-TR') === trimmed.toLocaleLowerCase('tr-TR'));
    if (existing) return existing;
    const atolye = { id: `atolye_${Date.now()}`, name: trimmed };
    try {
      setAtolyeler(await updateAtolyeler(list => [...list, atolye]));
      return atolye;
    } catch (error) {
      console.error('Atölye eklenemedi:', error);
      alert('Atölye eklenemedi. Atölye eklemek için Admin veya Satışcı yetkisi gerekir.');
      return null;
    }
  };

  const handleAddAtolye = async () => {
    const atolye = await addAtolye(newAtolyeName);
    if (atolye) {
      setNewAtolyeName('');
      setSelectedAtolyeId(atolye.id);
    }
  };

  const handleRenameAtolye = async () => {
    const name = (editingAtolye?.name || '').trim();
    if (!name) return;
    try {
      setAtolyeler(await updateAtolyeler(list => list.map(a => (a.id === editingAtolye.id ? { ...a, name } : a))));
      setEditingAtolye(null);
    } catch (error) {
      console.error('Atölye adı değiştirilemedi:', error);
      alert('Atölye adı değiştirilemedi. Bu işlem için Admin veya Satışcı yetkisi gerekir.');
    }
  };

  const handleDeleteAtolye = async () => {
    if (!deleteConfirmAtolye) return;
    try {
      setAtolyeler(await updateAtolyeler(list => list.filter(a => a.id !== deleteConfirmAtolye.id)));
      if (selectedAtolyeId === deleteConfirmAtolye.id) setSelectedAtolyeId('all');
      setDeleteConfirmAtolye(null);
    } catch (error) {
      console.error('Atölye silinemedi:', error);
      alert('Atölye silinemedi. Bu işlem için Admin veya Satışcı yetkisi gerekir.');
    }
  };

  // Aktif sekmeye göre kategoriler
  const currentCategories = activeSubTab === 'atelier' ? ATELIER_CATEGORIES
    : activeSubTab === 'montaj' ? MONTAJ_CATEGORIES
    : activeSubTab === 'firma' ? FIRMA_CATEGORIES
    : STORE_CATEGORIES;

  // Filtrelenmiş ve Sıralanmış Giderler
  const filteredExpenses = useMemo(() => {
    return expenses
      .filter((item) => {
        // Sekme filtresi (varsayılan 'store')
        const itemType = item.type || 'store';
        if (itemType !== activeSubTab) return false;

        // Atölye filtresi
        if (activeSubTab === 'atelier' && selectedAtolyeId !== 'all' && item.atolyeId !== selectedAtolyeId) {
          return false;
        }

        // Firma filtresi
        if (activeSubTab === 'firma' && selectedCompanyId !== 'all' && item.companyId !== selectedCompanyId) {
          return false;
        }

        // Arama filtresi
        if (searchTerm.trim() !== '') {
          const term = searchTerm.toLowerCase();
          const titleMatch = (item.title || '').toLowerCase().includes(term);
          const categoryMatch = (item.category || '').toLowerCase().includes(term);
          const vendorMatch = (item.vendor || '').toLowerCase().includes(term) ||
            getExpensePartyName(item).toLowerCase().includes(term);
          const notesMatch = (item.notes || '').toLowerCase().includes(term);
          if (!titleMatch && !categoryMatch && !vendorMatch && !notesMatch) return false;
        }

        // Kategori filtresi
        if (selectedCategory !== 'all' && item.category !== selectedCategory) {
          return false;
        }

        // Ödeme durumu filtresi
        if (selectedStatus !== 'all' && item.status !== selectedStatus) {
          return false;
        }

        // Tarih Aralığı Filtresi
        if (dateRange !== 'all' && item.date) {
          const itemDate = new Date(item.date);
          const now = new Date();

          if (dateRange === 'this-month') {
            if (
              itemDate.getMonth() !== now.getMonth() ||
              itemDate.getFullYear() !== now.getFullYear()
            ) {
              return false;
            }
          } else if (dateRange === 'last-month') {
            const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            if (
              itemDate.getMonth() !== lastMonth.getMonth() ||
              itemDate.getFullYear() !== lastMonth.getFullYear()
            ) {
              return false;
            }
          } else if (dateRange === 'this-year') {
            if (itemDate.getFullYear() !== now.getFullYear()) {
              return false;
            }
          }
        }

        return true;
      })
      .sort((a, b) => {
        if (sortOrder === 'date-desc') {
          return new Date(b.date || 0) - new Date(a.date || 0);
        }
        if (sortOrder === 'date-asc') {
          return new Date(a.date || 0) - new Date(b.date || 0);
        }
        if (sortOrder === 'amount-desc') {
          return (parseFloat(b.amount) || 0) - (parseFloat(a.amount) || 0);
        }
        if (sortOrder === 'amount-asc') {
          return (parseFloat(a.amount) || 0) - (parseFloat(b.amount) || 0);
        }
        return 0;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenses, activeSubTab, selectedAtolyeId, atolyeler, selectedCompanyId, companies, searchTerm, selectedCategory, selectedStatus, dateRange, sortOrder]);

  // Sayfalama Hesaplamaları
  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage) || 1;
  const paginatedExpenses = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredExpenses.slice(start, start + itemsPerPage);
  }, [filteredExpenses, currentPage, itemsPerPage]);

  // Sayfa değiştiğinde üst sınıra dikkat et
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedStatus, dateRange, sortOrder, activeSubTab, selectedAtolyeId, selectedCompanyId]);

  // İstatistik Hesaplamaları
  const stats = useMemo(() => {
    let total = 0;
    let thisMonthTotal = 0;
    let paidTotal = 0;
    let pendingTotal = 0;
    let pendingCount = 0;

    const now = new Date();

    filteredExpenses.forEach((item) => {
      const amt = parseFloat(item.amount) || 0;
      total += amt;

      if (item.status === 'paid') {
        paidTotal += amt;
      } else if (item.status === 'pending') {
        pendingTotal += amt;
        pendingCount += 1;
      }

      if (item.date) {
        const d = new Date(item.date);
        if (d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()) {
          thisMonthTotal += amt;
        }
      }
    });

    return { total, thisMonthTotal, paidTotal, pendingTotal, pendingCount };
  }, [filteredExpenses]);

  // Atölye bazında toplamlar (tüm atölye giderleri üzerinden)
  const atolyeStats = useMemo(() => {
    const result = {};
    expenses.forEach((item) => {
      if (item.type !== 'atelier') return;
      const key = item.atolyeId || 'none';
      if (!result[key]) result[key] = { total: 0, paid: 0, pending: 0, count: 0 };
      const amt = parseFloat(item.amount) || 0;
      result[key].total += amt;
      result[key].count += 1;
      if (item.status === 'paid') result[key].paid += amt;
      else if (item.status === 'pending') result[key].pending += amt;
    });
    return result;
  }, [expenses]);

  const allAtolyeStats = useMemo(() => Object.values(atolyeStats).reduce((acc, st) => ({
    total: acc.total + st.total, paid: acc.paid + st.paid, pending: acc.pending + st.pending, count: acc.count + st.count
  }), { total: 0, paid: 0, pending: 0, count: 0 }), [atolyeStats]);

  // Firma carisi: her firmaya ödenen / bekleyen (borç) toplamları
  const companyStats = useMemo(() => {
    const result = {};
    expenses.forEach((item) => {
      if (item.type !== 'firma') return;
      const key = item.companyId || 'none';
      if (!result[key]) result[key] = { total: 0, paid: 0, pending: 0, count: 0 };
      const amt = parseFloat(item.amount) || 0;
      result[key].total += amt;
      result[key].count += 1;
      if (item.status === 'paid') result[key].paid += amt;
      else if (item.status === 'pending') result[key].pending += amt;
    });
    return result;
  }, [expenses]);

  const allCompanyStats = useMemo(() => Object.values(companyStats).reduce((acc, st) => ({
    total: acc.total + st.total, paid: acc.paid + st.paid, pending: acc.pending + st.pending, count: acc.count + st.count
  }), { total: 0, paid: 0, pending: 0, count: 0 }), [companyStats]);

  // Modal Aç (Yeni Ekleme veya Düzenleme)
  const handleOpenModal = (expenseToEdit = null) => {
    fetchAtolyeler()
      .then(setAtolyeler)
      .catch(error => console.error('Atölyeler yüklenirken hata oluştu:', error));
    if (activeSubTab === 'firma') fetchCompanies();
    if (expenseToEdit) {
      setEditingExpense(expenseToEdit);
      setFormData({
        title: expenseToEdit.title || '',
        category: expenseToEdit.category || currentCategories[0],
        amount: expenseToEdit.amount ? String(expenseToEdit.amount) : '',
        date: expenseToEdit.date ? formatDateForInput(expenseToEdit.date) : formatDateForInput(new Date()),
        status: expenseToEdit.status || 'paid',
        paymentMethod: expenseToEdit.paymentMethod || 'nakit',
        vendor: expenseToEdit.vendor || '',
        atolyeId: expenseToEdit.atolyeId || '',
        companyId: expenseToEdit.companyId || '',
        notes: expenseToEdit.notes || ''
      });
    } else {
      setEditingExpense(null);
      setFormData({
        title: '',
        category: currentCategories[0],
        amount: '',
        date: formatDateForInput(new Date()),
        status: 'paid',
        paymentMethod: 'nakit',
        vendor: '',
        atolyeId: activeSubTab === 'atelier' && selectedAtolyeId !== 'all' ? selectedAtolyeId : '',
        companyId: activeSubTab === 'firma' && selectedCompanyId !== 'all' ? selectedCompanyId : '',
        notes: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingExpense(null);
  };

  // Form Gönderimi (Ekle / Güncelle)
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.amount || parseFloat(formData.amount) <= 0) {
      alert('Lütfen geçerli bir gider başlığı ve tutarı giriniz.');
      return;
    }
    const selectedAtolye = atolyeler.find(a => a.id === formData.atolyeId);
    if (activeSubTab === 'atelier' && !selectedAtolye) {
      alert('Lütfen bir atölye seçiniz.');
      return;
    }
    // Düzenlemede firma listeden silinmişse kayıttaki firma bilgisi korunur
    const selectedCompany = companies.find(c => c.id === formData.companyId) ||
      (editingExpense?.companyId === formData.companyId && formData.companyId
        ? { id: editingExpense.companyId, name: editingExpense.companyName || editingExpense.vendor || '' }
        : null);
    if (activeSubTab === 'firma' && !selectedCompany) {
      alert('Lütfen bir firma seçiniz.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        type: activeSubTab,
        title: formData.title.trim(),
        category: formData.category,
        amount: parseFloat(formData.amount),
        date: formData.date,
        status: formData.status,
        paymentMethod: formData.paymentMethod,
        vendor: activeSubTab === 'atelier' ? selectedAtolye.name : activeSubTab === 'firma' ? selectedCompany.name : formData.vendor.trim(),
        ...(activeSubTab === 'atelier' ? { atolyeId: selectedAtolye.id, atolyeName: selectedAtolye.name } : {}),
        ...(activeSubTab === 'firma' ? { companyId: selectedCompany.id, companyName: selectedCompany.name } : {}),
        notes: formData.notes.trim(),
        updatedAt: new Date().toISOString()
      };

      if (editingExpense) {
        // Güncelleme
        await updateDoc(doc(db, 'expenses', editingExpense.id), payload);
      } else {
        // Yeni Ekleme
        payload.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'expenses'), payload);
      }

      await fetchExpenses();
      handleCloseModal();
    } catch (error) {
      console.error('Kayıt kaydedilirken hata oluştu:', error);
      alert('İşlem gerçekleştirilirken bir hata oluştu.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Silme Onayı & İşlemi
  const handleDeleteExpense = async () => {
    if (!deleteConfirmExpense) return;
    try {
      await deleteDoc(doc(db, 'expenses', deleteConfirmExpense.id));
      await fetchExpenses();
      setDeleteConfirmExpense(null);
    } catch (error) {
      console.error('Gider silinirken hata oluştu:', error);
      alert('Silme işlemi başarısız oldu.');
    }
  };

  // CSV Export
  const exportToCSV = () => {
    if (filteredExpenses.length === 0) {
      alert('Dışa aktarılacak veri bulunamadı.');
      return;
    }

    let csvContent = 'data:text/csv;charset=utf-8,\uFEFF';
    csvContent += `Tarih,Başlık,Kategori,Tutar (TL),Ödeme Durumu,Ödeme Yöntemi,${activeSubTab === 'atelier' ? 'Atölye' : activeSubTab === 'firma' ? 'Firma' : 'Tedarikçi/Firma'},Notlar\n`;

    filteredExpenses.forEach((row) => {
      const statusText = row.status === 'paid' ? 'Ödendi' : 'Bekliyor';
      const methodObj = PAYMENT_METHODS.find((m) => m.id === row.paymentMethod);
      const methodText = methodObj ? methodObj.label : row.paymentMethod || '-';

      const line = [
        `"${row.date || ''}"`,
        `"${(row.title || '').replace(/"/g, '""')}"`,
        `"${row.category || ''}"`,
        `"${row.amount || 0}"`,
        `"${statusText}"`,
        `"${methodText}"`,
        `"${getExpensePartyName(row).replace(/"/g, '""')}"`,
        `"${(row.notes || '').replace(/"/g, '""')}"`
      ].join(',');

      csvContent += line + '\n';
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `${SUBTAB_FILE_NAMES[activeSubTab] || 'Magaza'}_Giderleri_${formatDateForInput(new Date())}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="expenses-container">
      {/* Üst Başlık & Sekme Geçişi */}
      <div className="expenses-header">
        <div className="expenses-title-group">
          <h2>
            <i className="fas fa-wallet"></i>
            Gider Yönetimi
          </h2>
          <p>Ay Home Lotus Mağaza ve Operasyonel harcamalarınızı kolayca takip ve organize edin</p>
        </div>

        <div className="expenses-subtabs">
          <button
            className={`subtab-btn ${activeSubTab === 'store' ? 'active' : ''}`}
            onClick={() => {
              setActiveSubTab('store');
              setSelectedCategory('all');
            }}
          >
            <i className="fas fa-store"></i>
            <span>Mağaza<span className="subtab-label-extra"> Giderleri</span></span>
          </button>

          <button
            className={`subtab-btn ${activeSubTab === 'atelier' ? 'active' : ''}`}
            onClick={() => {
              setActiveSubTab('atelier');
              setSelectedCategory('all');
            }}
          >
            <i className="fas fa-cut"></i>
            <span>Atölye<span className="subtab-label-extra"> Giderleri</span></span>
          </button>

          <button
            className={`subtab-btn ${activeSubTab === 'montaj' ? 'active' : ''}`}
            onClick={() => {
              setActiveSubTab('montaj');
              setSelectedCategory('all');
            }}
          >
            <i className="fas fa-tools"></i>
            <span>Montaj<span className="subtab-label-extra"> Giderleri</span></span>
          </button>

          <button
            className={`subtab-btn ${activeSubTab === 'orderCosts' ? 'active' : ''}`}
            onClick={() => setActiveSubTab('orderCosts')}
          >
            <i className="fas fa-receipt"></i>
            <span className="subtab-label-extra">Sipariş Maliyetleri</span>
            <span className="subtab-label-short">Maliyetler</span>
          </button>

          <button
            className={`subtab-btn ${activeSubTab === 'firma' ? 'active' : ''}`}
            onClick={() => {
              setActiveSubTab('firma');
              setSelectedCategory('all');
            }}
          >
            <i className="fas fa-building"></i>
            <span>Firma<span className="subtab-label-extra"> Giderleri</span></span>
          </button>
        </div>
      </div>

      {/* Sipariş Maliyetleri: yalnızca kontrol, hiçbir hesaba eklenmez */}
      {activeSubTab === 'orderCosts' ? (
        <OrderCosts />
      ) : (
        <>
        {/* Atölyeler: her atölyenin toplam / ödenen / bekleyen tutarları. Karta tıklayınca liste o atölyeye göre süzülür */}
        {activeSubTab === 'atelier' && (
          <div className="atolye-section">
            <div className="atolye-section-header">
              <h3><i className="fas fa-cut"></i> Atölyeler</h3>
              <div className="atolye-add-inline">
                <input
                  type="text"
                  placeholder="Yeni atölye adı"
                  value={newAtolyeName}
                  onChange={(e) => setNewAtolyeName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddAtolye(); } }}
                />
                <button type="button" onClick={handleAddAtolye} disabled={!newAtolyeName.trim()}>
                  <i className="fas fa-plus"></i> Atölye Ekle
                </button>
              </div>
            </div>

            <div className="atolye-cards">
              <button
                type="button"
                className={`atolye-card ${selectedAtolyeId === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedAtolyeId('all')}
              >
                <span className="atolye-card-name">Tüm Atölyeler</span>
                <span className="atolye-card-total">{formatPrice(allAtolyeStats.total)} ₺</span>
                <span className="atolye-card-row"><span>Ödenen</span><strong>{formatPrice(allAtolyeStats.paid)} ₺</strong></span>
                <span className="atolye-card-row"><span>Bekleyen</span><strong className="pending">{formatPrice(allAtolyeStats.pending)} ₺</strong></span>
                <span className="atolye-card-count">{allAtolyeStats.count} kayıt</span>
              </button>

              {atolyeler.map((atolye) => {
                const st = atolyeStats[atolye.id] || { total: 0, paid: 0, pending: 0, count: 0 };
                const isEditing = editingAtolye?.id === atolye.id;
                return (
                  <div
                    key={atolye.id}
                    role="button"
                    tabIndex={0}
                    className={`atolye-card ${selectedAtolyeId === atolye.id ? 'active' : ''}`}
                    onClick={() => !isEditing && setSelectedAtolyeId(atolye.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !isEditing) setSelectedAtolyeId(atolye.id); }}
                  >
                    {isEditing ? (
                      <div className="atolye-card-edit" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="text"
                          value={editingAtolye.name}
                          autoFocus
                          onChange={(e) => setEditingAtolye({ ...editingAtolye, name: e.target.value })}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleRenameAtolye(); if (e.key === 'Escape') setEditingAtolye(null); }}
                        />
                        <button type="button" onClick={handleRenameAtolye} title="Kaydet"><i className="fas fa-check"></i></button>
                        <button type="button" onClick={() => setEditingAtolye(null)} title="Vazgeç"><i className="fas fa-times"></i></button>
                      </div>
                    ) : (
                      <span className="atolye-card-name">
                        {atolye.name}
                        <span className="atolye-card-actions" onClick={(e) => e.stopPropagation()}>
                          <button type="button" onClick={() => setEditingAtolye({ id: atolye.id, name: atolye.name })} title="Adı düzenle">
                            <i className="fas fa-pen"></i>
                          </button>
                          <button type="button" onClick={() => setDeleteConfirmAtolye({ ...atolye, count: st.count })} title="Atölyeyi sil">
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </span>
                      </span>
                    )}
                    <span className="atolye-card-total">{formatPrice(st.total)} ₺</span>
                    <span className="atolye-card-row"><span>Ödenen</span><strong>{formatPrice(st.paid)} ₺</strong></span>
                    <span className="atolye-card-row"><span>Bekleyen</span><strong className="pending">{formatPrice(st.pending)} ₺</strong></span>
                    <span className="atolye-card-count">{st.count} kayıt</span>
                  </div>
                );
              })}
            </div>

            {atolyeler.length === 0 && (
              <p className="atolye-empty-hint">Henüz atölye yok. Yukarıdan atölye ekleyin ya da gider eklerken atölye adını yazın.</p>
            )}
          </div>
        )}

        {/* Firmalar (cari): her firmaya ödenen / bekleyen tutarlar. Karta tıklayınca liste o firmaya göre süzülür */}
        {activeSubTab === 'firma' && (
          <div className="atolye-section firma-section">
            <div className="atolye-section-header">
              <h3><i className="fas fa-building"></i> Firmalar</h3>
              <span className="firma-section-hint">
                <i className="fas fa-info-circle"></i> Firmalar Anasayfa &gt; Firma Bilgileri'nden eklenir. Bu kayıtlar net kazanca eklenmez.
              </span>
            </div>

            <div className="atolye-cards">
              <button
                type="button"
                className={`atolye-card ${selectedCompanyId === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedCompanyId('all')}
              >
                <span className="atolye-card-name">Tüm Firmalar</span>
                <span className="atolye-card-total">{formatPrice(allCompanyStats.total)} ₺</span>
                <span className="atolye-card-row"><span>Ödenen</span><strong>{formatPrice(allCompanyStats.paid)} ₺</strong></span>
                <span className="atolye-card-row"><span>Bekleyen</span><strong className="pending">{formatPrice(allCompanyStats.pending)} ₺</strong></span>
                <span className="atolye-card-count">{allCompanyStats.count} kayıt</span>
              </button>

              {companies.map((company) => {
                const st = companyStats[company.id] || { total: 0, paid: 0, pending: 0, count: 0 };
                return (
                  <button
                    key={company.id}
                    type="button"
                    className={`atolye-card ${selectedCompanyId === company.id ? 'active' : ''}`}
                    onClick={() => setSelectedCompanyId(company.id)}
                  >
                    <span className="atolye-card-name">{company.name}</span>
                    <span className="atolye-card-total">{formatPrice(st.total)} ₺</span>
                    <span className="atolye-card-row"><span>Ödenen</span><strong>{formatPrice(st.paid)} ₺</strong></span>
                    <span className="atolye-card-row"><span>Bekleyen</span><strong className="pending">{formatPrice(st.pending)} ₺</strong></span>
                    <span className="atolye-card-count">{st.count} kayıt</span>
                  </button>
                );
              })}
            </div>

            {companies.length === 0 && (
              <p className="atolye-empty-hint">Henüz firma yok. Anasayfa &gt; Firma Bilgileri'nden firma ekleyin.</p>
            )}
          </div>
        )}

        {/* Dinamik Özet Kartları */}
        <div className="expenses-stats-grid">
          <div className="expense-stat-card">
            <div className="stat-icon-wrapper total">
              <i className="fas fa-coins"></i>
            </div>
            <div className="stat-info">
              <div className="stat-label">
                Toplam {activeSubTab === 'atelier'
                  ? (selectedAtolyeId === 'all' ? 'Atölye' : atolyeler.find(a => a.id === selectedAtolyeId)?.name || 'Atölye')
                  : activeSubTab === 'firma'
                    ? (selectedCompanyId === 'all' ? 'Firma' : companies.find(c => c.id === selectedCompanyId)?.name || 'Firma')
                    : SUBTAB_LABELS[activeSubTab]} Gideri
              </div>
              <div className="stat-value">{formatPrice(stats.total)} ₺</div>
              <div className="stat-sub">{filteredExpenses.length} Kayıt Gösteriliyor</div>
            </div>
          </div>

          <div className="expense-stat-card">
            <div className="stat-icon-wrapper month">
              <i className="fas fa-calendar-alt"></i>
            </div>
            <div className="stat-info">
              <div className="stat-label">Bu Ayki Harcamalar</div>
              <div className="stat-value">{formatPrice(stats.thisMonthTotal)} ₺</div>
              <div className="stat-sub">Cari Ay Dönemi</div>
            </div>
          </div>

          <div className="expense-stat-card">
            <div className="stat-icon-wrapper paid">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-info">
              <div className="stat-label">Ödenen Toplam</div>
              <div className="stat-value">{formatPrice(stats.paidTotal)} ₺</div>
              <div className="stat-sub">Tamamlanmış Ödemeler</div>
            </div>
          </div>

          <div className="expense-stat-card">
            <div className="stat-icon-wrapper pending">
              <i className="fas fa-clock"></i>
            </div>
            <div className="stat-info">
              <div className="stat-label">Bekleyen Ödemeler</div>
              <div className="stat-value">{formatPrice(stats.pendingTotal)} ₺</div>
              <div className="stat-sub">{stats.pendingCount} Adet Bekleyen</div>
            </div>
          </div>
        </div>

        {/* Tek Satır Tüm Kontroller Araç Çubuğu */}
        <div className="expenses-toolbar">
          <div className="toolbar-inline-group">
            {/* Arama */}
            <div className="search-box">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Gider veya firma ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Kategori Filtresi */}
            <ModernSelect
              className="filter-select"
              ariaLabel="Kategori"
              value={selectedCategory}
              onChange={setSelectedCategory}
              options={[{ value: 'all', label: 'Tüm Kategoriler' }, ...currentCategories.map((cat) => ({ value: cat, label: cat }))]}
            />

            {/* Ödeme Durumu */}
            <ModernSelect
              className="filter-select"
              ariaLabel="Ödeme durumu"
              value={selectedStatus}
              onChange={setSelectedStatus}
              options={[
                { value: 'all', label: 'Tüm Ödeme Durumları' },
                { value: 'paid', label: 'Ödendi' },
                { value: 'pending', label: 'Bekliyor' }
              ]}
            />

            {/* Tarih Aralığı */}
            <ModernSelect
              className="filter-select"
              ariaLabel="Tarih aralığı"
              value={dateRange}
              onChange={setDateRange}
              options={[
                { value: 'all', label: 'Tüm Zamanlar' },
                { value: 'this-month', label: 'Bu Ay' },
                { value: 'last-month', label: 'Geçen Ay' },
                { value: 'this-year', label: 'Bu Yıl' }
              ]}
            />

            {/* Sıralama */}
            <ModernSelect
              className="filter-select"
              ariaLabel="Sıralama"
              value={sortOrder}
              onChange={setSortOrder}
              options={[
                { value: 'date-desc', label: 'Tarih: Yeni - Eski' },
                { value: 'date-asc', label: 'Tarih: Eski - Yeni' },
                { value: 'amount-desc', label: 'Tutar: Yüksek - Düşük' },
                { value: 'amount-asc', label: 'Tutar: Düşük - Yüksek' }
              ]}
            />
          </div>

          {/* Aksiyon Butonları */}
          <div className="toolbar-actions">
            <button className="btn-export-csv" onClick={exportToCSV} title="Excel / CSV Aktar">
              <i className="fas fa-file-excel" style={{ color: '#16a34a', fontSize: '1rem' }}></i>
              <span>Excel Aktar</span>
            </button>

            <button className="btn-add-expense" onClick={() => handleOpenModal(null)}>
              <i className="fas fa-plus"></i>
              <span>Yeni Gider Ekle</span>
            </button>
          </div>
        </div>

        {/* Tablo Kartı */}
        <div className="expenses-table-card">
          {loading ? (
            <div className="expenses-loading">
              <div className="expense-spinner"></div>
              <p>Giderler Yükleniyor...</p>
            </div>
          ) : paginatedExpenses.length === 0 ? (
            <div className="empty-expense-state">
              <div className="empty-expense-icon">
                <i className="fas fa-receipt"></i>
              </div>
              <h3>Gider Kaydı Bulunamadı</h3>
              <p>
                Arama kriterlerinize uygun veya henüz kayıtlı {SUBTAB_LABELS[activeSubTab]} gideri bulunmuyor.
              </p>
              <button className="btn-add-expense" style={{ margin: '0 auto' }} onClick={() => handleOpenModal(null)}>
                <i className="fas fa-plus"></i>
                İlk Gideri Ekle
              </button>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="expenses-table">
                  <thead>
                    <tr>
                      <th>Tarih</th>
                      <th>Gider Başlığı & {activeSubTab === 'atelier' ? 'Atölye' : 'Firma'}</th>
                      <th>Kategori</th>
                      <th>Tutar</th>
                      <th>Ödeme Yöntemi</th>
                      <th>Durum</th>
                      <th style={{ textAlign: 'right' }}>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedExpenses.map((expense) => {
                      const methodObj = PAYMENT_METHODS.find((m) => m.id === expense.paymentMethod);
                      const methodText = methodObj ? methodObj.label : expense.paymentMethod || 'Nakit';

                      return (
                        <tr key={expense.id}>
                          <td data-label="Tarih" style={{ fontWeight: '600', color: '#475569' }}>
                            {formatDate(expense.date)}
                          </td>
                          <td data-label="Gider Başlığı & Firma">
                            <div className="expense-title-cell">
                              <span className="expense-title-main">{expense.title}</span>
                              {getExpensePartyName(expense) && (
                                <span className="expense-vendor-sub">
                                  <i className={activeSubTab === 'atelier' ? 'fas fa-cut' : 'fas fa-building'} style={{ marginRight: '4px' }}></i>
                                  {getExpensePartyName(expense)}
                                </span>
                              )}
                              {expense.notes && (
                                <span style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' }}>
                                  {expense.notes}
                                </span>
                              )}
                            </div>
                          </td>
                          <td data-label="Kategori">
                            <span className="category-badge">
                              <i className="fas fa-tag" style={{ fontSize: '0.75rem' }}></i>
                              {expense.category}
                            </span>
                          </td>
                          <td data-label="Tutar" className="amount-cell">{formatPrice(expense.amount)} ₺</td>
                          <td data-label="Ödeme Yöntemi">
                            <span className="method-badge">
                              <i className="fas fa-credit-card" style={{ fontSize: '0.75rem' }}></i>
                              {methodText}
                            </span>
                          </td>
                          <td data-label="Durum">
                            {expense.status === 'paid' ? (
                              <span className="status-badge paid">
                                <i className="fas fa-check"></i> Ödendi
                              </span>
                            ) : (
                              <span className="status-badge pending">
                                <i className="fas fa-clock"></i> Bekliyor
                              </span>
                            )}
                          </td>
                          <td data-label="İşlemler" style={{ textAlign: 'right' }}>
                            <div className="action-buttons" style={{ justifyContent: 'flex-end' }}>
                              <button
                                className="btn-icon-action edit"
                                onClick={() => handleOpenModal(expense)}
                                title="Düzenle"
                              >
                                <i className="fas fa-pen"></i>
                              </button>
                              <button
                                className="btn-icon-action delete"
                                onClick={() => setDeleteConfirmExpense(expense)}
                                title="Sil"
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              <div className="pagination-container">
                <div className="pagination-info">
                  Toplam <strong>{filteredExpenses.length}</strong> kayıttan{' '}
                  <strong>{(currentPage - 1) * itemsPerPage + 1}</strong> -{' '}
                  <strong>{Math.min(currentPage * itemsPerPage, filteredExpenses.length)}</strong> arası gösteriliyor
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <ModernSelect
                    className="filter-select ex-page-size"
                    ariaLabel="Sayfa başına kayıt"
                    value={itemsPerPage}
                    onChange={(v) => {
                      setItemsPerPage(Number(v));
                      setCurrentPage(1);
                    }}
                    options={[10, 25, 50, 100].map((n) => ({ value: n, label: `${n} Sayfa Başına` }))}
                  />

                  <div className="pagination-controls">
                    <button
                      className="btn-page"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                      // Sayfa numaraları çok fazlaysa sınırlayalım
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNum}
                            className={`btn-page ${currentPage === pageNum ? 'active' : ''}`}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                      if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                        return <span key={pageNum} style={{ color: '#94a3b8' }}>...</span>;
                      }
                      return null;
                    })}

                    <button
                      className="btn-page"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        </>
      )}

      {/* Ekleme / Düzenleme Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="expense-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-bar">
              <h3>
                <i className={editingExpense ? 'fas fa-edit' : 'fas fa-plus-circle'}></i>
                {editingExpense ? 'Gider Kaydını Düzenle' : 'Yeni Gider Kaydı Ekle'}
              </h3>
              <button className="btn-close-modal" onClick={handleCloseModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmitForm}>
              <div className="modal-form-body">
                <div className="form-field-group">
                  <label htmlFor="title">Gider Başlığı / Açıklama *</label>
                  <input
                    type="text"
                    id="title"
                    placeholder={activeSubTab === 'atelier' ? 'Örn: Ekim ayı dikim ödemesi' : activeSubTab === 'montaj' ? 'Örn: Kadıköy montajı yol ve yakıt' : activeSubTab === 'firma' ? 'Örn: Ekim ayı kumaş ödemesi' : 'Örn: Şubat Ayı Mağaza Kirası'}
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>

                <div className="form-grid-2">
                  <div className="form-field-group">
                    <label htmlFor="category">Kategori *</label>
                    <ModernSelect
                      id="category"
                      fullWidth
                      value={formData.category}
                      onChange={(v) => setFormData({ ...formData, category: v })}
                      options={currentCategories.map((cat) => ({ value: cat, label: cat }))}
                    />
                  </div>

                  <div className="form-field-group">
                    <label htmlFor="amount">Tutar (₺) *</label>
                    <input
                      type="number"
                      id="amount"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-grid-2">
                  <div className="form-field-group">
                    <label htmlFor="date">Tarih *</label>
                    <input
                      type="date"
                      id="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-field-group">
                    <label htmlFor="status">Ödeme Durumu *</label>
                    <ModernSelect
                      id="status"
                      fullWidth
                      value={formData.status}
                      onChange={(v) => setFormData({ ...formData, status: v })}
                      options={[
                        { value: 'paid', label: 'Ödendi' },
                        { value: 'pending', label: 'Bekliyor / Ödenmedi' }
                      ]}
                    />
                  </div>
                </div>

                <div className="form-grid-2">
                  <div className="form-field-group">
                    <label htmlFor="paymentMethod">Ödeme Yöntemi</label>
                    <ModernSelect
                      id="paymentMethod"
                      fullWidth
                      value={formData.paymentMethod}
                      onChange={(v) => setFormData({ ...formData, paymentMethod: v })}
                      options={PAYMENT_METHODS.map((m) => ({ value: m.id, label: m.label }))}
                    />
                  </div>

                  {activeSubTab === 'atelier' ? (
                    <div className="form-field-group">
                      <label htmlFor="atolye">Atölye *</label>
                      <Select
                        inputId="atolye"
                        className="atolye-select"
                        classNamePrefix="atolye-select"
                        options={atolyeler.map(a => ({ value: a.id, label: a.name }))}
                        value={atolyeler.filter(a => a.id === formData.atolyeId).map(a => ({ value: a.id, label: a.name }))[0] || null}
                        onChange={(option) => setFormData({ ...formData, atolyeId: option ? option.value : '' })}
                        placeholder={atolyeler.length ? 'Atölye seçin' : 'Önce atölye ekleyin'}
                        noOptionsMessage={() => 'Henüz atölye yok. Atölyeler bölümünden ekleyin.'}
                        isSearchable={false}
                        menuPortalTarget={document.body}
                        styles={atolyeSelectStyles}
                      />
                      {atolyeler.length === 0 && (
                        <small style={{ color: '#d97706' }}>
                          Kayıtlı atölye yok. Pencereyi kapatıp üstteki "Atölye Ekle" ile atölye ekleyin.
                        </small>
                      )}
                    </div>
                  ) : activeSubTab === 'firma' ? (
                    <div className="form-field-group">
                      <label htmlFor="company">Firma *</label>
                      <Select
                        inputId="company"
                        className="atolye-select"
                        classNamePrefix="atolye-select"
                        options={companies.map(c => ({ value: c.id, label: c.name }))}
                        value={
                          companies.filter(c => c.id === formData.companyId).map(c => ({ value: c.id, label: c.name }))[0] ||
                          (formData.companyId && editingExpense?.companyId === formData.companyId
                            ? { value: formData.companyId, label: editingExpense.companyName || editingExpense.vendor || 'Silinmiş firma' }
                            : null)
                        }
                        onChange={(option) => setFormData({ ...formData, companyId: option ? option.value : '' })}
                        placeholder={companies.length ? 'Firma seçin' : 'Önce firma ekleyin'}
                        noOptionsMessage={() => 'Firma bulunamadı. Anasayfa > Firma Bilgileri\'nden ekleyin.'}
                        isSearchable
                        menuPortalTarget={document.body}
                        styles={atolyeSelectStyles}
                      />
                      {companies.length === 0 && (
                        <small style={{ color: '#d97706' }}>
                          Kayıtlı firma yok. Anasayfa &gt; Firma Bilgileri'nden firma ekleyin.
                        </small>
                      )}
                    </div>
                  ) : (
                    <div className="form-field-group">
                      <label htmlFor="vendor">{activeSubTab === 'montaj' ? 'Montajcı / Firma / Kişi' : 'Tedarikçi / Firma / Kişi'}</label>
                      <input
                        type="text"
                        id="vendor"
                        placeholder={activeSubTab === 'montaj' ? 'Örn: Montajcı adı, akaryakıt istasyonu' : 'Örn: Enerjisa, Mal Sahibi vb.'}
                        value={formData.vendor}
                        onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                      />
                    </div>
                  )}
                </div>

                <div className="form-field-group">
                  <label htmlFor="notes">Notlar / Ek Bilgiler</label>
                  <textarea
                    id="notes"
                    rows="2"
                    placeholder="Eklemek istediğiniz notları yazabilirsiniz..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
              </div>

              <div className="modal-footer-actions">
                <button type="button" className="btn-modal-cancel" onClick={handleCloseModal}>
                  İptal
                </button>
                <button type="submit" className="btn-modal-submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Kaydediliyor...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check"></i> {editingExpense ? 'Güncelle' : 'Kaydet'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Atölye Silme Onay Modal */}
      {deleteConfirmAtolye && (
        <div className="modal-overlay" onClick={() => setDeleteConfirmAtolye(null)}>
          <div className="delete-confirm-card" onClick={(e) => e.stopPropagation()}>
            <div className="delete-icon-circle">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h3>Atölyeyi Sil</h3>
            <p>
              <strong>"{deleteConfirmAtolye.name}"</strong> atölyesi listeden silinsin mi?
              {deleteConfirmAtolye.count > 0 && ` Bu atölyeye ait ${deleteConfirmAtolye.count} gider kaydı silinmez; maliyet ve net kazanç hesaplarında kalmaya devam eder.`}
            </p>
            <div className="delete-actions">
              <button className="btn-modal-cancel" onClick={() => setDeleteConfirmAtolye(null)}>
                Vazgeç
              </button>
              <button className="btn-delete-confirm" onClick={handleDeleteAtolye}>
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Silme Onay Modal */}
      {deleteConfirmExpense && (
        <div className="modal-overlay" onClick={() => setDeleteConfirmExpense(null)}>
          <div className="delete-confirm-card" onClick={(e) => e.stopPropagation()}>
            <div className="delete-icon-circle">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h3>Gider Kaydını Sil</h3>
            <p>
              <strong>"{deleteConfirmExpense.title}"</strong> başlıklı ({formatPrice(deleteConfirmExpense.amount)} ₺) gider kaydını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div className="delete-actions">
              <button className="btn-modal-cancel" onClick={() => setDeleteConfirmExpense(null)}>
                Vazgeç
              </button>
              <button className="btn-delete-confirm" onClick={handleDeleteExpense}>
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
